// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Mise en forme de la facturation : argent, dates, taux, refus, documents.
 * Partagée par l'écran des factures et celui des avoirs, pour qu'une facture et
 * l'avoir qui l'annule s'affichent au même format.
 *
 * Rien ici ne calcule : les montants arrivent de l'API en centimes entiers, et
 * seul le backend fait l'arithmétique de l'argent (un arrondi sur un flottant
 * JavaScript redonnerait un centime d'écart).
 */
import { saveFile } from './fichiers';

const FMT_EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export const NOT_PROVIDED = 'non renseigné';

/**
 * Centimes vers « 1 234,56 € ». Les séparateurs sont ramenés à l'espace
 * insécable : selon le moteur, Intl rend une espace fine ou une espace
 * ordinaire, et un montant coupé en fin de ligne se lit mal sur une facture.
 */
export function euros(cents) {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return NOT_PROVIDED;
  return FMT_EUR.format(Number(cents) / 100).replace(/\s/g, ' ');
}

/** Points de base vers pourcentage français : 1040 devient « 10,40 % ». */
export function percentBp(bp) {
  if (bp === null || bp === undefined) return NOT_PROVIDED;
  return `${(Number(bp) / 100).toFixed(2).replace('.', ',')} %`;
}

export function dateFr(iso) {
  if (!iso) return NOT_PROVIDED;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('fr-FR');
}

export function timestampFr(iso) {
  if (!iso) return NOT_PROVIDED;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('fr-FR');
}

export function quantityFr(q) {
  if (q === null || q === undefined) return NOT_PROVIDED;
  return String(q).replace('.', ',');
}

/** Euros saisis vers centimes entiers. Rend null si la saisie n'est pas un nombre. */
export function toCents(input) {
  if (input === '' || input === null || input === undefined) return null;
  const n = Number(String(input).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

/**
 * Message lisible tiré du `detail` de l'API, qui peut être une chaîne (cas
 * courant), la liste de validation de Pydantic ou l'objet structuré d'un refus
 * (émission refusée avec ses `missing`, avoir refusé avec ses `reasons`,
 * canal non branché avec ses étapes). Affiché brut, un objet donnerait
 * « [object Object] ». Les autres écrans passent par `utils/erreurs`, qui ne
 * connaît pas ces refus propres à la facturation.
 */
export function billingErrorMessage(e, fallback = 'Erreur inattendue') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x?.msg ? `${(x.loc || []).slice(1).join('.')} : ${x.msg}` : String(x))).join(' ; ');
  }
  if (d && typeof d === 'object') {
    const parts = [];
    if (d.message) parts.push(d.message);
    if (Array.isArray(d.manquantes) && d.manquantes.length) parts.push(`Manquant : ${d.manquantes.join(' ; ')}`);
    if (Array.isArray(d.motifs) && d.motifs.length) parts.push(d.motifs.join(' ; '));
    if (parts.length) return parts.join('. ');
  }
  if (e?.response?.status) return `${fallback} (code ${e.response.status})`;
  return fallback;
}

/** L'objet structuré du 501 de dépôt, quand c'en est un. */
export function channelNotConnectedDetail(e) {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 501 && d && typeof d === 'object' && d.canal) return d;
  return null;
}

/** La liste des mentions manquantes du 422 d'émission, quand c'en est une. */
export function missingFromRejection(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === 'object' && Array.isArray(d.manquantes)) return d.manquantes;
  return null;
}

/**
 * Télécharge un document de facturation (facture ou avoir), en Markdown ou en PDF.
 *
 * Passe par le client axios, jamais par une ancre ni un `window.open` : ces
 * routes sont authentifiées par en-tête, et un lien nu partirait sans le jeton
 * (403).
 *
 * Rend une explication en cas d'échec, null sinon.
 */
export async function downloadDocument(api, path, fileName, format) {
  try {
    const r = await api.get(`${path}/document.${format}`, { responseType: 'blob' });
    const type = format === 'pdf' ? 'application/pdf' : 'text/markdown;charset=utf-8';
    saveFile(new Blob([r.data], { type }), `${fileName}.${format}`);
    return null;
  } catch (e) {
    return billingErrorMessage(e, 'Téléchargement impossible');
  }
}

/**
 * Régime de TVA de l'émetteur, tel que le serveur le déclare.
 *
 * Franchise en base par défaut (art. 293 B du CGI), sans numéro de TVA. Le
 * backend lit VIGEOS_TVA_REGIME (« franchise » ou « assujetti ») et sert
 * `tva_regime` et `mention_tva` avec les coordonnées de l'émetteur. Un serveur
 * qui ne les sert pas est lu en franchise : l'inverse ferait imprimer une TVA
 * que l'émetteur n'a pas le droit de facturer.
 */
export const VAT_EXEMPTION_NOTICE = 'TVA non applicable, art. 293 B du CGI';

export function vatRegime(issuer) {
  const brut = String(issuer?.tva_regime || '').trim().toLowerCase();
  const vatLiable = brut === 'assujetti';
  const mention = String(issuer?.mention_tva || '').trim();
  return {
    assujetti: vatLiable,
    franchise: !vatLiable,
    declare: brut !== '',
    mention: mention || (vatLiable ? '' : VAT_EXEMPTION_NOTICE),
  };
}
